import React, { Component } from 'react';
import { GameContext } from '../../contexts/GameContext';
import constants from '../../config';
import { resolveCardImage } from '../../utils/resolveImage';
import { formatSealedType } from '../../utils/sealedTypes';

class CardIdentify extends Component {
  static contextType = GameContext;
  constructor(props) {
    super();
    this.state = {
      cards: [],
      search: '',
      loading: false,
      showDropdown: false,
    };
    this.debounceTimer = null;
    this.handleSearch = this.handleSearch.bind(this);
  }

  componentWillUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  handleSearch(e) {
    const query = e.target.value;
    this.setState({ search: query });

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (query.trim().length < 2) {
      this.setState({ cards: [], showDropdown: false, loading: false });
      return;
    }

    this.setState({ loading: true });
    this.debounceTimer = setTimeout(() => {
      this.fetchCards(query.trim());
    }, 400);
  }

  fetchCards(query) {
    const gameSlug = this.context && this.context.currentGame && this.context.currentGame.slug;
    const gameParam = gameSlug ? `&game=${encodeURIComponent(gameSlug)}` : '';
    const productTypeParam = this.props.productType ? `&productType=${encodeURIComponent(this.props.productType)}` : '';
    const endpoint = `${constants.DOMAIN}/api/v1/guest/cards/search?search=${encodeURIComponent(query)}&limit=20${gameParam}${productTypeParam}`;
    fetch(endpoint, {
      mode: 'cors',
      method: 'GET',
    })
      .then(response => response.json())
      .then(res => {
        this.setState({
          cards: res.cards || [],
          showDropdown: true,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  selectCard(card) {
    this.setState({ search: card.name, showDropdown: false });
    if (this.props.onCardSelected) {
      this.props.onCardSelected(card);
    }
  }

  render() {
    const { cards, search, loading, showDropdown } = this.state;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var inputStyle = {
      width: '100%',
      padding: '10px 12px',
      paddingLeft: '32px',
      fontSize: '14px',
      borderRadius: '8px',
      border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
      outline: 'none',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#1a1a1a',
    };
    var isSealed = this.props.productType === 'sealed';
    return (
      <div style={{ position: 'relative', marginTop: '16px' }}>
        <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
          {isSealed ? 'Search sealed products' : 'Search for a card'}
        </label>
        <div style={{ position: 'relative' }}>
          <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-[#999]" style={{ transform: 'translateY(-50%)' }}>
            <i className={'fa ' + (isSealed ? 'fa-cube' : 'fa-search') + ' tw-text-sm'} />
          </span>
          <input
            type="text"
            value={search}
            onChange={this.handleSearch}
            placeholder={isSealed ? 'Search sealed catalog...' : 'Type a card name...'}
            style={inputStyle}
            className="focus:tw-border-brand-gold"
          />
          {loading && (
            <span className="tw-absolute tw-right-3 tw-top-1/2 tw-text-xs tw-text-[#999]" style={{ transform: 'translateY(-50%)' }}>
              Searching...
            </span>
          )}
        </div>

        {showDropdown && cards.length > 0 && (
          <div
            className="tw-absolute tw-left-0 tw-right-0 tw-rounded-xl tw-mt-1 tw-shadow-xl tw-z-50 tw-overflow-hidden tw-border"
            style={isDark
              ? { backgroundColor: '#1a1a1a', borderColor: '#555', maxHeight: '320px', overflowY: 'auto' }
              : { backgroundColor: '#fff', borderColor: '#e0e0e0', maxHeight: '320px', overflowY: 'auto' }}
          >
            {cards.map(card => {
              const cardImg = resolveCardImage(card.image);

              return (
                <button
                  key={card._id}
                  type="button"
                  onClick={() => this.selectCard(card)}
                  className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-left tw-transition-colors"
                  onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = isDark ? '#252525' : '#f5f5f5'; }}
                  onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {cardImg && (
                    <img
                      src={cardImg}
                      alt={card.name}
                      className="tw-w-10 tw-h-14 tw-object-contain tw-rounded tw-flex-shrink-0"
                    />
                  )}
                  <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-text-sm tw-font-medium tw-mb-0 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                      {card.name}
                      {card.variant ? (' (' + card.variant + ')') : ''}
                    </p>
                    <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
                      {card.cardSet || ''}
                      {isSealed && card.sealedType ? (' \u2022 ' + formatSealedType(card.sealedType)) : ''}
                      {!isSealed && card.rarity ? (' \u2022 ' + card.rarity) : ''}
                    </p>
                  </div>
                  {card.lowestPrice != null && card.lowestPrice > 0 && (
                    <span className="tw-text-xs tw-font-semibold tw-text-brand-gold tw-flex-shrink-0">
                      ${Number(card.lowestPrice).toFixed(2)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {showDropdown && cards.length === 0 && !loading && search.trim().length >= 2 && (
          <div
            className="tw-absolute tw-left-0 tw-right-0 tw-rounded-xl tw-mt-1 tw-shadow-xl tw-z-50 tw-p-4 tw-text-center tw-border"
            style={isDark
              ? { backgroundColor: '#1a1a1a', borderColor: '#555' }
              : { backgroundColor: '#fff', borderColor: '#e0e0e0' }}
          >
            <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
              No cards found
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default CardIdentify;
